import type { SpiffProcessInstanceRow } from "@/api/dto/process-instance";
import { formatCaseNumber } from "@/lib/format/case-number";

import type { CaseListItem, CurrentStep } from "./case";
import type { CaseConfig } from "./case-config";
import { deriveCaseStatus } from "./derive-case-status";
import { metadataOf } from "./metadata";

/** 一覧行 → UI ドメイン型。 */
export function toCaseListItem(
  row: SpiffProcessInstanceRow,
  config: CaseConfig,
): CaseListItem {
  const metadata = Object.fromEntries(
    config.metadataKeys.map((key) => [key, metadataOf(row, key)]),
  );

  return {
    caseId: row.id,
    caseNumber: formatCaseNumber(row.id),
    // モデル表示名で埋めない。件名の検索は API 側（件名 metadata への contains）で
    // 行うため、埋めると「画面に見えている文字で検索しても 0 件」になる。
    // 種別は processModelDisplayName（案件種別の列）で見せる。
    title: metadata[config.titleMetadataKey],
    processModelId: row.process_model_identifier,
    processModelDisplayName: row.process_model_display_name,
    initiator: row.process_initiator_username ?? null,
    status: deriveCaseStatus(row, config),
    rawStatus: row.status,
    currentStep: toCurrentStep(row),
    submittedAt: epochToDate(row.start_in_seconds),
    completedAt: epochToDate(row.end_in_seconds),
    updatedAt: epochToDate(row.updated_at_in_seconds),
    lastMilestone: row.last_milestone_bpmn_name ?? null,
    metadata,
  };
}

/**
 * epoch 秒 → Date。SpiffArena は未設定を 0 で返すことがあるため 0 も null に寄せる
 * （1970-01-01 が画面に出るのを防ぐ）。
 */
export function epochToDate(seconds: number | null | undefined): Date | null {
  if (seconds === null || seconds === undefined || seconds === 0) return null;
  return new Date(seconds * 1000);
}

function toCurrentStep(row: SpiffProcessInstanceRow): CurrentStep | null {
  // with_oldest_open_task を付けていないと一切載らない
  if (row.task_id === undefined && row.task_name === undefined) return null;

  const name = emptyToNull(row.task_name);

  return {
    taskGuid: emptyToNull(row.task_id),
    name,
    label: emptyToNull(row.task_title) ?? name,
    waitingFor: emptyToNull(row.waiting_for),
  };
}

function emptyToNull(value: string | null | undefined): string | null {
  return value === null || value === undefined || value === "" ? null : value;
}
